import { ScrollArea, Stack, Text } from '@mantine/core'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import RootButton from '@/components/Buttons/RootButton'
import UserDetails from '@/components/Details/UserDetails/UserDetails'
import { ApplicationFormData, sectionQuestions } from '@/components/Governance/ApplicationForm'
import Loading from '@/components/Loading'
import TooltipTitle from '@/components/TooltipTitle'
import { useGovernanceContext } from '@/contexts/GovernanceContext'
import { useAuth } from '@/plugins/auth'
import { fetchIpfsMetadata } from '@/utils/utils'

import ModalSkeleton from '../ModalSkeleton'

interface ViewApplicationModalProps {
  nominee: string
  fillApplicationPeriodEnded?: boolean
  close: () => void
}

const ViewApplicationModal: React.FC<ViewApplicationModalProps> = ({
  nominee,
  close,
  fillApplicationPeriodEnded = true,
}) => {
  const { t } = useTranslation()
  const { address } = useAuth()
  const { nomineeApplication } = useGovernanceContext()
  const [applicationContent, setApplicationContent] = useState<ApplicationFormData | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const nomineeCid = useMemo(() => nomineeApplication(nominee), [nomineeApplication, nominee])

  // get content from nomineeCid
  useEffect(() => {
    const fetchContent = async () => {
      setIsLoading(true)
      try {
        const content = await fetchIpfsMetadata(`ipfs://${nomineeCid}`)
        setApplicationContent(content)
        console.log('Fetched content:', content)
        setIsLoading(false)
        setError('')
      } catch (err: any) {
        console.log('Error fetching metadata', error)
        setIsLoading(false)
        setError(err.message)
      }
    }
    fetchContent()
  }, [nomineeCid])

  // This would be replaced with actual application data
  const applicationSections: ApplicationFormData = {
    vision:
      'Vision details here. This is a long text to show how it will look like when the text is long and it needs to wrap to the next line.',
    contribution:
      'Contribution details here. This is a long text to show how it will look like when the text is long and it needs to wrap to the next line.',
    experience:
      'Experience details here. This is a long text to show how it will look like when the text is long and it needs to wrap to the next line.',
    running:
      'Running details here. This is a long text to show how it will look like when the text is long and it needs to wrap to the next line.',
  }

  return (
    <ModalSkeleton
      opened
      onClose={close}
      title={t('modals.viewApplication.title', 'View Application')}
    >
      <Stack gap="30px">
        <UserDetails userId={nominee} width="fit-content" />
        <ScrollArea h={400} style={{ maxWidth: '400px' }}>
          {Object.entries(applicationContent || applicationSections).map(([key, value]) => (
            <Stack
              key={key}
              style={{
                gap: '5px',
                padding: sectionQuestions[key as keyof ApplicationFormData]?.subQuestion
                  ? '10px 12px 0 0'
                  : '10px 0',
              }}
            >
              <TooltipTitle
                order={5}
                title={sectionQuestions[key as keyof ApplicationFormData].question}
                tooltip={sectionQuestions[key as keyof ApplicationFormData]?.subQuestion}
              />
              {isLoading ? (
                <Loading size="14px" style={{ alignSelf: 'flex-start' }} />
              ) : (
                <Text style={{ fontSize: 14, maxWidth: '400px' }}>{value}</Text>
              )}
            </Stack>
          ))}
        </ScrollArea>

        <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
          <RootButton onClick={close} secondary style1={false} expand>
            {t('buttons.close', 'Close')}
          </RootButton>
          {address === nominee && !fillApplicationPeriodEnded && (
            <RootButton onClick={() => window.open('/application', '_blank')} style1={false} expand>
              {t('buttons.edit', 'Edit')}
            </RootButton>
          )}
        </Stack>
      </Stack>
    </ModalSkeleton>
  )
}

export default ViewApplicationModal
